import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import shortid from 'shortid'
import classNames from 'classnames'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
import ToolTip from './tooltip'
// import Row, { Slot, LabelSlot, FieldSlot } from './row'
import { LabelSlot, FieldSlot } from './row'

import Input, { Label, ClickEditInput } from './input'
import styles from '../component-styles/campaign-codes-administration.module.scss'
import Button, { ButtonRow } from './button'
import { GeneratorFields, GeneratorSelectFields } from '../api-client/api-types'

import { getItemByKeyValue } from '../helpers'
import { checkParamNameIsInvalid } from '../helpers/campaign-codes'
import { MultiSelectBox } from './select-box'
import ToggleBox from './toggle-box'
import { updateGeneratorParameterAsync } from '../store/actions'

import { fieldTypes } from '../core/variables'
import SelectTable from './campaign-codes-select-table'

export function CopiedFromLabel({ items }: { items: any }): React.ReactElement {
  if (items.length === 0) {
    return <></>
  }

  if (items.length === 1) {
    return (
      <>
        <span className={styles.optional}>(hidden)</span>
        <span className={styles.optional}>
          (copied from {items.join(', ')})
        </span>
      </>
    )
  }
  if (items.length <= 2) {
    return (
      <>
        <span className={styles.optional}>(hidden)</span>
        <span className={styles.optional}>
          (copied from {items.slice(0, -1).join(', ')} and {items.slice(-1)})
        </span>
      </>
    )
  }
  return (
    <>
      <span className={styles.optional}>(hidden)</span>
      <span className={styles.optional}>
        (copied from {items.slice(0, 1).join(', ')} and {items.length - 1} more)
      </span>
    </>
  )
}

export interface CampaignCodesAdministrationToggleBoxProps {
  // updateParamDef: (item: GeneratorFields) => void
  onRemove: (fieldName: string) => void
  paramDef: GeneratorFields
  parentsList: GeneratorFields[]
  copyFromValues?: GeneratorFields[]
  allParams: string[]
}

export default function CampaignCodesAdministrationToggleBox({
  paramDef,
  // updateParamDef,
  onRemove,
  copyFromValues = [],
  parentsList,
  allParams,
}: CampaignCodesAdministrationToggleBoxProps): React.ReactElement {
  const [nextParamDef, setNextParamDef] = useState(paramDef)
  const [hasChildren, setHasChildren] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [active, setActive] = useState(false)
  const [restrictDropdowns, setRestrictDropdowns] = useState(false)
  const [boxKey, setBoxKey] = useState('')

  useEffect(() => {
    setBoxKey(shortid.generate())
  }, [])

  const dispatch = useDispatch()

  useEffect((): void => {
    setNextParamDef(paramDef)
  }, [paramDef])

  const {
    fieldName,
    prefix,
    helpText,
    selectFields,
    fieldType,
    required,
    copyFromField,
    metaParameter,
    forceLowerCase = true,
    fieldID,
  } = nextParamDef

  const parentID = useMemo(() => {
    let parent = ''
    let _hasChildren = false
    if (selectFields && selectFields.length > 0) {
      selectFields.forEach((item) => {
        if (
          Object.prototype.hasOwnProperty.call(item, 'optionFilter') &&
          item.optionFilter &&
          item.optionFilter.length > 0
        ) {
          // TODO: this ignores th epossibility of having other parents...
          parent = item.optionFilter[0].parentFieldID
          if (item.optionFilter[0].parentOptionIDs.length > 0) {
            _hasChildren = true
          }
        }
      })
      if (!_hasChildren) {
        parent = ''
      }
    }
    if (parent !== '') {
      setRestrictDropdowns(true)
    }
    setHasChildren(_hasChildren)
    return parent
  }, [paramDef, selectFields])

  // const setNextParamDef = (newParamDef) => {
  // setNextParamDef(newParamDef)
  // updateParamDef(newParamDef)
  // }

  const updateSelectFields = (
    nextSelectField: GeneratorSelectFields,
    index = -1,
  ): void => {
    // eslint-disable-next-line
    // debugger
    let newParamDef
    if (index === -1) {
      newParamDef = {
        ...nextParamDef,
        selectFields: nextParamDef.selectFields
          ? [...nextParamDef.selectFields, nextSelectField]
          : [nextSelectField],
      }
    } else {
      newParamDef = {
        ...nextParamDef,
      }
      const updateIndex = getItemByKeyValue(
        newParamDef.selectFields,
        'optionID',
        nextSelectField.optionID,
        true,
      )
      if (updateIndex !== -1) {
        newParamDef.selectFields[updateIndex] = nextSelectField
      }
    }
    setNextParamDef(newParamDef)
  }

  const updateGeneratorParam = (updatedField) => {
    // eslint-disable-next-line
    // debugger
    let newParamDef
    if (Array.isArray(updatedField)) {
      newParamDef = {
        ...nextParamDef,
        selectFields: [...updatedField],
      }
    } else {
      newParamDef = {
        ...nextParamDef,
      }
      const updateIndex = getItemByKeyValue(
        newParamDef.selectFields,
        'optionID',
        updatedField.optionID,
        true,
      )
      if (updateIndex !== -1) {
        newParamDef.selectFields[updateIndex] = updatedField
      }
    }
    setNextParamDef(newParamDef)
    return
    dispatch(updateGeneratorParameterAsync(newParamDef))
  }

  useEffect((): void => {
    if (!selectFields || (selectFields && selectFields.length === 0)) {
      setShowAdd(true)
    }
  }, [selectFields])

  const toggleActive = () => {
    setActive(!active)
  }
  // const copyFromFieldItem = copyFromField
  //   ? getItemByKeyValue(copyFromValues, 'fieldID', copyFromField)
  //   : null

  const copyFromFieldItem = copyFromField
    ? copyFromField.map((item) => {
        const found = getItemByKeyValue(copyFromValues, 'fieldID', item)
        return found !== -1 ? found.fieldName : ''
      })
    : null

  useEffect((): void => {
    if (
      fieldType === 'input' &&
      copyFromFieldItem &&
      copyFromFieldItem.length > 0 &&
      required
    ) {
      setNextParamDef({
        ...nextParamDef,
        required: false,
      })
    }
    // eslint-disable-next-line
  }, [fieldType, copyFromFieldItem, required])

  const useParentList: GeneratorFields[] = useMemo(() => {
    return parentsList.filter((item: GeneratorFields) => {
      const { fieldID: fId } = item
      return fieldID !== fId
    })
  }, [parentsList])

  const [fieldNameError, setFieldNameError] = useState(false)
  const [notUpdated, setNotUpdated] = useState(false)

  return (
    <ToggleBox
      className={styles.accordion}
      key={`${paramDef.fieldID}`}
      type="white"
      heading={
        <>
          <span className={styles.headingWrapper}>
            <span className={styles.titlePart}>
              <span className={styles.title}>
                <ClickEditInput
                  showCancel
                  id={`${boxKey} fieldName`}
                  name={`${boxKey} fieldName`}
                  value={paramDef.fieldName}
                  onChange={(value: string): void => {
                    if (!fieldNameError) {
                      setNextParamDef({
                        ...nextParamDef,
                        fieldName: value,
                      })
                      dispatch(
                        updateGeneratorParameterAsync({
                          fieldID: nextParamDef.fieldID,
                          fieldName: value,
                        }),
                      )
                    } else {
                      setNotUpdated(true)
                      window.setTimeout(() => {
                        setNotUpdated(false)
                      }, 2000)
                      setFieldNameError(false)
                    }
                  }}
                  beforeChange={(value: string): string => {
                    checkParamNameIsInvalid(
                      value,
                      allParams,
                      setFieldNameError,
                      nextParamDef.fieldName,
                    )
                    return value
                  }}
                  disabled={fieldNameError}
                />
              </span>
              {!required &&
                !(
                  fieldType === 'input' &&
                  copyFromFieldItem &&
                  copyFromFieldItem.length > 0
                ) && <span className={styles.optional}>(optional)</span>}
              {fieldType === 'input' && copyFromFieldItem && (
                <CopiedFromLabel items={copyFromFieldItem} />
              )}
            </span>
            <span className={styles.typeHeading}>
              {fieldTypes[fieldType]
                ? fieldTypes[fieldType].long
                : fieldTypes[''].long}
              {fieldType === 'date' && paramDef.dateFormat !== '' ? (
                <span className={styles.small}>({paramDef.dateFormat})</span>
              ) : (
                ''
              )}
            </span>
          </span>
          {fieldNameError && (
            <p className={styles.footNoteError}>
              A parameter with that name already exists.
            </p>
          )}
          {notUpdated && (
            <p className={styles.footNoteError}>
              Not updated as parameter name already exists.
            </p>
          )}
        </>
      }
    >
      {fieldType === 'select' && selectFields && (
        <p className={styles.intro}>
          Add or remove options for this parameter using the table below.
        </p>
      )}
      <div className={styles.settingHeaders}>
        <div className={styles.settingHeader}>
          <LabelSlot column>
            <Label id={`${boxKey} prefix`}>Prefix</Label>
            <ToolTip message="The query string parameter name which always appears before the typed or selected value. Example: utm_campaign= is the prefix for the Google Analytics campaign value. This may be a symbol when concatenating multiple inputs into one parameter." />
          </LabelSlot>
          <FieldSlot column>
            <ClickEditInput
              id={`${boxKey} prefix`}
              name={`${boxKey} prefix`}
              value={prefix}
              onChange={(value: string): void => {
                setNextParamDef({
                  ...nextParamDef,
                  prefix: value,
                })
                dispatch(
                  updateGeneratorParameterAsync({
                    fieldID: nextParamDef.fieldID,
                    prefix: value,
                  }),
                )
              }}
            />
          </FieldSlot>
        </div>
        <div className={styles.settingHeader}>
          <LabelSlot column>
            <Label id={`${boxKey} helpText`}>Help text</Label>
            <ToolTip message="Text designed to help users enter the right values on the Track>Create page. Text will show when the users hovers over a tooltip next to each parameter name." />
          </LabelSlot>
          <FieldSlot column>
            <ClickEditInput
              id={`${boxKey} helpText`}
              className={styles.clickEditInput}
              name={`${boxKey} helpText`}
              value={helpText}
              onChange={(value: string): void => {
                setNextParamDef({
                  ...nextParamDef,
                  helpText: value,
                })
                dispatch(
                  updateGeneratorParameterAsync({
                    fieldID: nextParamDef.fieldID,
                    helpText: value,
                  }),
                )
              }}
            />
          </FieldSlot>
        </div>
        <div
          className={classNames(styles.settingHeader, {
            [styles.disabled]: !!(
              fieldType === 'input' &&
              copyFromFieldItem &&
              copyFromFieldItem.length > 0
            ),
          })}
        >
          <LabelSlot column>
            <Label id={`${boxKey} required`}>Required</Label>
            <ToolTip message="If checked, users will have to complete this field to create a campaign link." />
          </LabelSlot>
          <FieldSlot column className={styles.checkboxOption}>
            <FieldSlot className={styles.bottomPadding}>
              <Input
                type="checkbox"
                id={`${boxKey} required`}
                name={`${boxKey} required`}
                label=" "
                checked={required}
                disabled={
                  !!(
                    fieldType === 'input' &&
                    copyFromFieldItem &&
                    copyFromFieldItem.length > 0
                  )
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  const { checked } = e.target as HTMLInputElement
                  setNextParamDef({
                    ...nextParamDef,
                    required: checked,
                  })
                  dispatch(
                    updateGeneratorParameterAsync({
                      fieldID: nextParamDef.fieldID,
                      required: checked,
                    }),
                  )
                }}
              />
            </FieldSlot>
          </FieldSlot>
        </div>
        <div className={styles.settingHeader}>
          <LabelSlot column>
            <Label id={`${boxKey} forceLowerCase`}>Lowercase</Label>
            <ToolTip message="If checked, users' text input will be automatically converted to lowercase when typing. This makes data easier to read in analytics platforms." />
          </LabelSlot>
          <FieldSlot column className={styles.checkboxOption}>
            <FieldSlot className={styles.bottomPadding}>
              <Input
                type="checkbox"
                id={`${boxKey} forceLowerCase`}
                name={`${boxKey} forceLowerCase`}
                label=" "
                checked={!!forceLowerCase}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  const { checked } = e.target as HTMLInputElement
                  setNextParamDef({
                    ...nextParamDef,
                    forceLowerCase: checked,
                  })
                  dispatch(
                    updateGeneratorParameterAsync({
                      fieldID: nextParamDef.fieldID,
                      forceLowerCase: checked,
                    }),
                  )
                }}
              />
            </FieldSlot>
          </FieldSlot>
        </div>
        <div className={styles.settingHeader}>
          <LabelSlot column>
            <Label id={`${boxKey} meta`}>Meta</Label>
            <ToolTip message="If checked, this parameter data will only be stored in Uplifter and will not be visible in your campaign link (or your analytics tools). Designed for campaign link notes." />
          </LabelSlot>
          <FieldSlot column className={styles.checkboxOption}>
            <FieldSlot className={styles.bottomPadding}>
              <Input
                type="checkbox"
                id={`${boxKey} meta`}
                name={`${boxKey} meta`}
                label=" "
                checked={!!metaParameter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  const { checked } = e.target as HTMLInputElement
                  setNextParamDef({
                    ...nextParamDef,
                    metaParameter: checked,
                  })
                  dispatch(
                    updateGeneratorParameterAsync({
                      fieldID: nextParamDef.fieldID,
                      metaParameter: checked,
                    }),
                  )
                }}
              />
            </FieldSlot>
          </FieldSlot>
        </div>
        {fieldType === 'input' && copyFromValues.length > 0 && (
          <div className={styles.settingHeader}>
            <LabelSlot column>
              <Label id={`${boxKey} copy`}>Copy from</Label>
            </LabelSlot>
            <FieldSlot column>
              <FieldSlot>
                <MultiSelectBox
                  id={`${boxKey} copy`}
                  className={styles.multiSelect}
                  value={copyFromField || []}
                  items={copyFromValues}
                  keyName="fieldName"
                  keyValue="fieldID"
                  onChange={(val) => {
                    setNextParamDef({
                      ...nextParamDef,
                      copyFromField: val,
                      required: false,
                    })
                    dispatch(
                      updateGeneratorParameterAsync({
                        fieldID: nextParamDef.fieldID,
                        required: false,
                        copyFromField: val.map((copyFromID) => {
                          return { copyFromID }
                        }),
                      }),
                    )
                  }}
                />
              </FieldSlot>
            </FieldSlot>
          </div>
        )}
        {fieldType === 'select' && selectFields && (
          <Button
            disabled={parentID !== '' && hasChildren}
            className={styles.restrictButton}
            type="pinkBorder"
            onClick={(): void => {
              setRestrictDropdowns(!restrictDropdowns)
            }}
          >
            {restrictDropdowns
              ? 'Don’t restrict dropdowns'
              : 'Restrict dropdowns'}
          </Button>
        )}
      </div>

      {fieldType === 'select' && selectFields && (
        <SelectTable
          fieldID={fieldID}
          hasChildren={hasChildren}
          parentID={parentID}
          parentsList={useParentList}
          restrictDropdowns={restrictDropdowns}
          forceLowerCase={forceLowerCase}
          showAdd={showAdd}
          setShowAdd={setShowAdd}
          fields={selectFields}
          fieldName={fieldName}
          onUpdateField={(options, index) => {
            updateSelectFields(options, index)
          }}
          updateGeneratorParam={(updatedField) => {
            updateGeneratorParam(updatedField)
          }}
          onDelete={(optionName) => {
            setNextParamDef({
              ...nextParamDef,
              selectFields: [...nextParamDef.selectFields].filter(
                (field): boolean => field.optionName !== optionName,
              ),
            })
          }}
        />
      )}

      <ButtonRow
        style={{
          justifyContent: 'flex-start',
        }}
        centerAlign
        className={styles.rowWithPadding}
      >
        <Button
          // className={styles.hide}
          type="pinkBorder"
          onClick={(): void => {
            setActive(true)
          }}
        >
          Remove parameter
        </Button>
      </ButtonRow>
      {active && (
        <Modal active={active} toggleActive={toggleActive} type="white" narrow>
          <ModalHead>
            <Heading type={3} align="left">
              Remove parameter
            </Heading>
          </ModalHead>
          <ModalBody>
            <p>
              Are you sure you want to permanently delete the{' '}
              <strong>{fieldName}</strong> parameter?
            </p>
            <p>
              This will remove all names, values and codes and cannot be
              reversed.
            </p>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              onYes={(): void => {
                setActive(false)
                if (fieldID) {
                  onRemove(fieldID)
                }
              }}
              onNo={(): void => {
                setActive(false)
              }}
              yesLabel="Yes"
              noLabel="Cancel"
            />
          </ModalFooter>
        </Modal>
      )}
    </ToggleBox>
  )
}
